/************************************************************************************
 * [MODULE]: PRODUCT INFORMATION
 * Main components:
 * - l-product-form: Handles form submission and cart interactions
 * - l-variant-selects: Manages variant selection and option changes
 * - l-product-controls: Controls product quantity and variant-specific logic
 * - l-product-quantity-input: Handles quantity input interactions
 *
 * Internal Events:
 * - l:variant:changed: Fired when the variant changes
 * - l:quantity:changed: Fired when the quantity changes
 * - l:product:unavailable: Fired when the product is unavailable
 * - l:variant:option-changed: Fired when the variant option changes
 * - l:sellingPlan:changed: Fired when the selling plan changes
 * - l:addon-products:changed: Fired when the addon products change
 * External Events:
 * - l:cart:closed: Fired when the cart is closed
 *************************************************************************************/

/************************************************************************************
 *
 * [MODULE]: PRODUCT MEDIA
 *
 *************************************************************************************/

if (!customElements.get('l-product-media-gallery')) {
	class ProductMediaGallery extends HTMLElement {
		constructor() {
			super()
			this.sectionId = this.dataset.sectionId
			this.productId = this.dataset.productId
			this._boundSetupCarousel = this.setupCarousel.bind(this)
			this._boundOnVariantChange = this.onVariantChange.bind(this)
		}

		connectedCallback() {
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			this._initModalZoomEvents()

			document.addEventListener('l:variant:changed', this._boundOnVariantChange)

			if (window?.themeCore?.utils?.Swiper) {
				this.setupCarousel()
			} else {
				document.addEventListener('l:themeCore:loaded', this._boundSetupCarousel)
			}
		}

		disconnectedCallback() {
			document.removeEventListener('l:vendor:loaded', this._boundSetupCarousel)
			document.removeEventListener('l:variant:changed', this._boundOnVariantChange)
		}

		_initModalZoomEvents() {
			const zoomMediaBtnEls = this.querySelectorAll('[data-modal-zoom-media]')
			zoomMediaBtnEls.forEach((btn) => {
				btn.addEventListener('click', () => {
					this.dispatchEvent(
						new CustomEvent('l:media:click', {
							bubbles: true,
							detail: { mediaId: btn.dataset.modalZoomMedia },
						})
					)
				})
			})
		}

		setupCarousel() {
			this._setupThumbnailCarousel()
			this._setupMainCarousel()
			this._syncCarousels()
			this._bindSlideChangeEvent()
		}

		_setupThumbnailCarousel() {
			this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
			this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
			this.thumbnailSlides.forEach((slide) => slide.classList.remove('l-hidden'))

			this.thumbnailCarouselSwiper = new window.themeCore.utils.Swiper(this.thumbnailCarousel, {
				loop: false,
				slidesPerView: 'auto',
				spaceBetween: 8,
				resistanceRatio: 0.2,
				navigation: {
					nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
					prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
				},
			})
		}

		_setupMainCarousel() {
			this.mainCarouselEl = this.querySelector('[data-main-carousel]')
			this.mediaSlidesEls = this.mainCarouselEl.querySelectorAll('[data-media]')
			this.navigationNextEl = this.mainCarouselEl.querySelector('[data-next]')
			this.navigationPrevEl = this.mainCarouselEl.querySelector('[data-prev]')

			this.mediaSlidesEls.forEach((slide) => slide.classList.remove('l-hidden'))

			this.mainCarouselSwiper = new window.themeCore.utils.Swiper(this.mainCarouselEl, {
				loop: false,
				slidesPerView: 'auto',
				spaceBetween: 0,
				autoHeight: true,
				keyboard: { enabled: true },
				navigation: {
					nextEl: this.navigationNextEl,
					prevEl: this.navigationPrevEl,
				},
				thumbs: {
					swiper: this.thumbnailCarouselSwiper,
				},
			})
		}

		_syncCarousels() {
			// if (this.mainCarouselSwiper && this.thumbnailCarouselSwiper) {
			// 	this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper
			// }
		}

		_bindSlideChangeEvent() {
			if (!this.mainCarouselSwiper) return
			this.mainCarouselSwiper.on('slideChange', (swiper) => {
				const activeSlideEl = this.mediaSlidesEls[swiper.activeIndex]
				l_pauseAllMedia()
				l_playMedia(activeSlideEl)
			})
		}

		onVariantChange(event) {
			const { variantId, variant } = event.detail || {}

			const featuredMediaId = variant?.featured_media?.id
			let activeSlideIndex = 0

			if (featuredMediaId) {
				activeSlideIndex = Array.from(this.mediaSlidesEls).findIndex(
					(mediaSlideEl) => mediaSlideEl.dataset.mediaId == `${featuredMediaId}`
				)
				if (activeSlideIndex === -1) activeSlideIndex = 0
			}

			this.mainCarouselSwiper.slideTo(activeSlideIndex, true)
		}
	}

	customElements.define('l-product-media-gallery', ProductMediaGallery)
}

/************************************************************************************
 *
 * [SECTIONS]: PRODUCT ADDITIONAL COMPONENTS
 *
 *************************************************************************************/

/************************************************************************************
 *
 * [SECTIONS]: PRODUCT ADDITIONAL SECTIONS
 *
 *************************************************************************************/

/******************************
 * [SECTION]: PRODUCT RECOMMENDATIONS
 *******************************/

if (!customElements.get('l-engraving-buttons')) {
	class LTAEngravingButtons extends HTMLElement {
		connectedCallback() {
			this._attachEvent()
		}

		_attachEvent() {
			const engravingButtonEl = document.querySelector('[data-engraving-button]')
			if (!engravingButtonEl) return

			engravingButtonEl.addEventListener('click', this._handleEngravingClick.bind(this))
		}

		_handleEngravingClick(event) {
			const buttonEl = document.querySelector('.pplr-c-button.pplr-btn')

			if (!buttonEl) return

			cstmfy_personalize_text(buttonEl, event)
		}
	}

	customElements.define('l-engraving-buttons', LTAEngravingButtons)
}

/******************************
 * [SECTION]: PRODUCT RECOMMENDATIONS
 *******************************/

if (!customElements.get('l-product-recommendations')) {
	class LTAProductRecommendations extends HTMLElement {
		constructor() {
			super()
			this._observer = null
		}

		connectedCallback() {
			this._initObserver()
		}

		disconnectedCallback() {
			this._observer?.disconnect()
		}

		_initObserver() {
			this._observer?.disconnect()
			this._observer = new IntersectionObserver(
				(entries, observer) => {
					const entry = entries[0]
					if (!entry.isIntersecting) return
					observer.unobserve(this)
					this._loadRecommendations()
				},
				{ rootMargin: '0px 0px 400px 0px' }
			)
			this._observer.observe(this)
		}

		async _loadRecommendations() {
			try {
				const response = await fetch(this.dataset.url)
				const text = await response.text()
				const html = document.createElement('div')
				html.innerHTML = text
				const recommendations = html.querySelector('l-product-recommendations')
				const hasRecommendations = recommendations && recommendations.innerHTML.trim().length > 0

				if (hasRecommendations) {
					this.innerHTML = recommendations.innerHTML
				} else if (this.parentElement) {
					this.parentElement.classList.add('l-hidden')
				}
			} catch (e) {
				console.error(e)
			}
		}
	}

	customElements.define('l-product-recommendations', LTAProductRecommendations)
}

/*****************************************
 * [SECTION]: PRODUCT COMPARE MEDIA
 ******************************************/

class LTACompareMedia extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.rangeInputEl = this.querySelector('input')

		// Events
		this.rangeInputEl.addEventListener('input', (e) => this.handleRangeInput(e))
	}

	handleRangeInput(e) {
		const percent = (parseInt(this.rangeInputEl.value, 10) / 1000) * 100
		this.style.setProperty('--l-percent', `${percent}%`)
	}
}

customElements.define('l-compare-media', LTACompareMedia)

/*****************************************
 * [SECTION]: PRODUCT SPECIFICATIONS
 ******************************************/

class LTAPDPSpecifications extends HTMLElement {
	constructor() {
		super()
		this.handleTabClick = this.handleTabClick.bind(this)
	}

	connectedCallback() {
		this.triggerTabEls = this.querySelectorAll('[data-trigger-tab]')
		this.tabEls = this.querySelectorAll('[data-tab]')
		this.addEventListeners()
	}

	addEventListeners() {
		this.triggerTabEls.forEach((triggerTabEl) => {
			triggerTabEl.addEventListener('click', this.handleTabClick)
		})
	}

	handleTabClick(e) {
		e.preventDefault()
		const clickedTab = e.currentTarget

		this.triggerTabEls.forEach((el) => {
			el.classList.toggle('is-active', el === clickedTab)
		})

		this.tabEls.forEach((el) => {
			el.classList.toggle('l-hidden', clickedTab.dataset.triggerTab !== el.dataset.tab)
		})
	}
}

customElements.define('l-pdp-specifications', LTAPDPSpecifications)

/*****************************************
 * [SECTION]: PRODUCT SPECIFICATIONS V2
 ******************************************/

class LTAPDPSpecificationsV2 extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		const productURL = this.dataset.productUrl
		const sectionId = l_extractSectionId(this)
		const specificationEl = this.querySelector('[data-specifications]')

		document.addEventListener('l:variant:changed', (event) => {
			const { variantId } = event.detail || {}

			fetch(`${productURL}?variant=${variantId}&section_id=${sectionId}`)
				.then((response) => response.text())
				.then((html) => {
					const sectionDOM = l_getSectionDOM(html, '[data-specifications]')

					specificationEl.innerHTML = sectionDOM.innerHTML
				})
		})
	}
}

customElements.define('l-pdp-specifications-v2', LTAPDPSpecificationsV2)

